/**
 * useLocation Hook
 *
 * Manages user location permission using browser's native prompt
 * Prompts every time there's no cached location (handles "Allow Only This Time")
 * Prompts after sign out/in if no cached location
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { requestUserLocation, getCachedLocation, clearLocation } from "../utils/locationUtils";
import logger from "../utils/logger";

const PERMISSION_STATUS_KEY = "locationPermissionStatus";

export const useLocation = () => {
  const [location, setLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [shouldRequestPermission, setShouldRequestPermission] = useState(false);
  const hasPromptedRef = useRef(false); // Track if we've prompted in this component lifecycle
  const lastUserIdRef = useRef(null); // Track last known user ID

  // Listen for login/logout events and reset prompt flag
  useEffect(() => {
    const checkUserChange = () => {
      try {
        const userStr = localStorage.getItem("user");
        const currentUserId = userStr ? JSON.parse(userStr)?.id : null;

        // If user ID changed (login/logout), reset the prompted flag
        if (currentUserId !== lastUserIdRef.current) {
          logger.log("User changed (login/logout), resetting location prompt flag");
          hasPromptedRef.current = false;
          lastUserIdRef.current = currentUserId;
        }
      } catch (e) {
        // Ignore parse errors
      }
    };

    // Check immediately
    checkUserChange();

    // Check every second for user changes
    const userCheckInterval = setInterval(checkUserChange, 1000);

    return () => clearInterval(userCheckInterval);
  }, []);

  // Check if we should request permission
  useEffect(() => {
    const checkPermissionStatus = () => {
      // Don't prompt more than once per component mount
      if (hasPromptedRef.current) {
        logger.log("Already prompted this mount, skipping");
        return;
      }

      // Check if user has valid cached location
      const cached = getCachedLocation();
      if (cached) {
        setLocation(cached);
        setHasPermission(true);
        setShouldRequestPermission(false);
        logger.log("Location already cached, not requesting");
        return;
      }

      // No cached location - check if permission was permanently denied
      const status = localStorage.getItem(PERMISSION_STATUS_KEY);
      if (status === "denied") {
        setShouldRequestPermission(false);
        logger.log("Location permission permanently denied, not requesting");
        return;
      }

      // All checks passed - should request permission
      logger.log("No cached location found, should request permission");
      setShouldRequestPermission(true);
    };

    // Check immediately on mount
    checkPermissionStatus();

    // Re-check every 5 seconds (catches when "Allow Only This Time" expires)
    const interval = setInterval(checkPermissionStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Request location permission from user
   * Triggers browser's native "wants to access your location" prompt
   */
  const requestLocation = useCallback(async () => {
    setIsLoading(true);
    hasPromptedRef.current = true; // Mark that we've prompted

    try {
      logger.log("Requesting user location (native browser prompt)...");
      const userLocation = await requestUserLocation();

      if (userLocation) {
        logger.log("Location granted:", userLocation);
        setLocation(userLocation);
        setHasPermission(true);
        setShouldRequestPermission(false);
        // Don't set to localStorage - let the utility function handle it
        return userLocation;
      } else {
        logger.log("Location permission denied by user");
        setHasPermission(false);
        setShouldRequestPermission(false);
        localStorage.setItem(PERMISSION_STATUS_KEY, "denied");
        return null;
      }
    } catch (error) {
      logger.error("Error requesting location:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear location data and reset permission
   * Useful for testing or if user wants to revoke
   */
  const resetLocation = useCallback(() => {
    clearLocation();
    localStorage.removeItem(PERMISSION_STATUS_KEY);
    setLocation(null);
    setHasPermission(false);
    setShouldRequestPermission(false);
    hasPromptedRef.current = false;
  }, []);

  return {
    location,
    hasPermission,
    isLoading,
    shouldRequestPermission,
    requestLocation,
    resetLocation,
  };
};

export default useLocation;
